import { useTranslation } from "react-i18next";
import React, { useState, Children } from 'react';
import { ArrowLeftIcon, PlusIcon, TrashIcon, AlertCircleIcon } from 'lucide-react';
interface AddRoomFormProps {
  onSave: (roomData: any) => void;
  onCancel: () => void;
}
export function AddRoomForm({
  onSave,
  onCancel
}: AddRoomFormProps) {
  const {
    t
  } = useTranslation("rooms");
  const [roomData, setRoomData] = useState({
    name: '',
    category: '',
    code: '',
    description: '',
    isSmoking: false,
    isAccessible: false,
    isActive: false,
    view: '',
    size: '',
    amenities: [] as string[],
    baseAdultsOccupancy: 2,
    baseChildrenOccupancy: 0,
    maxOccupancy: 2,
    allowAdditionalAdults: false,
    allowAdditionalChildren: false,
    bedConfigurations: [{
      type: '',
      count: 1
    }]
  });
  const roomCategories = ['SUPERIOR', 'DELUXE', 'OVERWATER_VILLA', 'BEACH_VILLA', 'SUITE'];
  const roomViews = ['SEA_VIEW', 'GARDEN_VIEW', 'OCEAN_VIEW', 'LAGOON_VIEW', 'BEACH_VIEW'];
  const amenityOptions = ['Wi-Fi', 'TV', 'Minibar', 'Air Conditioning', 'Balcony', 'Terrace', 'Ocean View', 'Private Pool', 'Jacuzzi', 'Slippers', 'Desk', 'Dining Area', 'Interconnecting Rooms', 'Non-Smoking', 'Beach Access', 'Breakfast Included'];
  const bedTypes = ['King', 'Queen', 'Double', 'Twin', 'Single', 'Sofa Bed', 'Bunk Bed'];
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setRoomData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      checked
    } = e.target;
    setRoomData(prev => ({
      ...prev,
      [name]: checked
    }));
  };
  const handleNumberChange = (name: string, value: string) => {
    const numValue = value === '' ? 0 : parseInt(value, 10);
    setRoomData(prev => ({
      ...prev,
      [name]: numValue
    }));
  };
  const handleBedConfigChange = (index: number, field: string, value: string) => {
    const updatedConfigs = [...roomData.bedConfigurations];
    if (field === 'count') {
      updatedConfigs[index] = {
        ...updatedConfigs[index],
        [field]: parseInt(value, 10) || 1
      };
    } else {
      updatedConfigs[index] = {
        ...updatedConfigs[index],
        [field]: value
      };
    }
    setRoomData(prev => ({
      ...prev,
      bedConfigurations: updatedConfigs
    }));
  };
  const addBedConfig = () => {
    setRoomData(prev => ({
      ...prev,
      bedConfigurations: [...prev.bedConfigurations, {
        type: '',
        count: 1
      }]
    }));
  };
  const removeBedConfig = (index: number) => {
    const updatedConfigs = [...roomData.bedConfigurations];
    updatedConfigs.splice(index, 1);
    setRoomData(prev => ({
      ...prev,
      bedConfigurations: updatedConfigs
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(roomData);
  };
  return <div className="space-y-6" data-id="element-9374">
      <div className="flex items-center" data-id="element-9375">
        <button onClick={onCancel} className="flex items-center text-gray-600 hover:text-gray-900" data-id="element-9376">
          
          <ArrowLeftIcon size={18} className="mr-1" data-id="element-9377" />
          <span data-id="element-9378">{t("components.AddRoomForm.add_room")}</span>
        </button>
      </div>
      <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-9379">
        
        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-9380">
          <div data-id="element-9381">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9382">
              
              {t("components.AddRoomForm.room_name")}<span className="text-red-500" data-id="element-9383">*</span>
            </label>
            <input type="text" id="name" name="name" value={roomData.name} onChange={handleInputChange} required className="w-full p-2 border border-gray-300 rounded-md" data-id="element-9384" />
            
          </div>
          <div data-id="element-9385">
            <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9386">
              
              {t("components.AddRoomForm.room_category")}<span className="text-red-500" data-id="element-9387">*</span>
            </label>
            <div className="relative" data-id="element-9388">
              <select id="category" name="category" value={roomData.category} onChange={handleInputChange} required className="w-full p-2 border border-gray-300 rounded-md appearance-none pr-8" data-id="element-9389">
                
                <option value="" data-id="element-9390">{t("components.AddRoomForm.select_room_category")}</option>
                {roomCategories.map(category => <option key={category} value={category} data-id="element-9391">
                    {category.replace('_', ' ')}
                  </option>)}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none" data-id="element-9392">
                <svg className="w-4 h-4 fill-current text-gray-500" viewBox="0 0 20 20" data-id="element-9393">
                  
                  <path d="M7 7l3-3 3 3m0 6l-3 3-3-3" strokeWidth="1.5" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round" data-id="element-9394" />
                  
                </svg>
              </div>
            </div>
          </div>
        </div>
        <div className="mb-6" data-id="element-9395">
          <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9396">
            
            {t("components.AddRoomForm.room_code")}
          </label>
          <input type="text" id="code" name="code" value={roomData.code} onChange={handleInputChange} placeholder={t("components.AddRoomForm.attrs.enter_room_code")} className="w-full p-2 border border-gray-300 rounded-md" data-id="element-9397" />
          
        </div>
        <div className="mb-6" data-id="element-9398">
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9399">
            
            {t("components.AddRoomForm.description")}
          </label>
          <textarea id="description" name="description" value={roomData.description} onChange={handleInputChange} placeholder={t("components.AddRoomForm.attrs.enter_room_description")} rows={4} className="w-full p-2 border border-gray-300 rounded-md" data-id="element-9400" />
          
        </div>
        <div className="flex flex-wrap gap-6 mb-6" data-id="element-9401">
          <div className="flex items-center" data-id="element-9402">
            <input type="checkbox" id="isSmoking" name="isSmoking" checked={roomData.isSmoking} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-9403" />
            
            <label htmlFor="isSmoking" className="ml-2 text-sm text-gray-700" data-id="element-9404">
              {t("components.AddRoomForm.smoking")}
            </label>
          </div>
          <div className="flex items-center" data-id="element-9405">
            <input type="checkbox" id="isAccessible" name="isAccessible" checked={roomData.isAccessible} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-9406" />
            
            <label htmlFor="isAccessible" className="ml-2 text-sm text-gray-700" data-id="element-9407">
              
              {t("components.AddRoomForm.accessibility")}
            </label>
          </div>
          <div className="flex items-center" data-id="element-9408">
            <input type="checkbox" id="isActive" name="isActive" checked={roomData.isActive} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-9409" />
            
            <label htmlFor="isActive" className="ml-2 text-sm text-gray-700" data-id="element-9410">
              {t("components.AddRoomForm.status")}
            </label>
          </div>
        </div>
        {/* Room View and Size */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-9411">
          <div data-id="element-9412">
            <label htmlFor="view" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9413">
              
              {t("components.AddRoomForm.room_view")}<span className="text-red-500" data-id="element-9414">*</span>
            </label>
            <div className="relative" data-id="element-9415">
              <select id="view" name="view" value={roomData.view} onChange={handleInputChange} required className="w-full p-2 border border-gray-300 rounded-md appearance-none pr-8" data-id="element-9416">
                
                <option value="" data-id="element-9417">{t("components.AddRoomForm.select_room_view")}</option>
                {roomViews.map(view => <option key={view} value={view} data-id="element-9418">
                    {view.replace('_', ' ')}
                  </option>)}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none" data-id="element-9419">
                <svg className="w-4 h-4 fill-current text-gray-500" viewBox="0 0 20 20" data-id="element-9420">
                  
                  <path d="M7 7l3-3 3 3m0 6l-3 3-3-3" strokeWidth="1.5" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round" data-id="element-9421" />
                  
                </svg>
              </div>
            </div>
          </div>
          <div data-id="element-9422">
            <label htmlFor="size" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9423">
              
              {t("components.AddRoomForm.room_size_sq_m")}
            </label>
            <input type="number" id="size" name="size" value={roomData.size} onChange={handleInputChange} min="0" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-9424" />
            
          </div>
        </div>
        {/* Room Amenities */}
        <div className="mb-6" data-id="element-9425">
          <label htmlFor="amenities" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9426">
            
            {t("components.AddRoomForm.room_amenities")}
          </label>
          <select id="amenities" name="amenities" multiple value={roomData.amenities} onChange={e => {
          const options = Array.from(e.target.selectedOptions, option => option.value);
          setRoomData(prev => ({
            ...prev,
            amenities: options
          }));
        }} className="w-full p-2 border border-gray-300 rounded-md" data-id="element-9427">
            
            {amenityOptions.map(amenity => <option key={amenity} value={amenity} data-id="element-9428">
                {amenity}
              </option>)}
          </select>
        </div>
        {/* Occupancy Settings */}
        <div className="mb-6" data-id="element-9429">
          <h3 className="text-md font-medium text-gray-800 mb-4" data-id="element-9430">
            {t("components.AddRoomForm.occupancy_settings")}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4" data-id="element-9431">
            <div data-id="element-9432">
              <label htmlFor="baseAdultsOccupancy" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9433">
                
                {t("components.AddRoomForm.base_adults_occupancy")}<span className="text-red-500" data-id="element-9434">*</span>
              </label>
              <input type="number" id="baseAdultsOccupancy" value={roomData.baseAdultsOccupancy} onChange={e => handleNumberChange('baseAdultsOccupancy', e.target.value)} min="1" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-9435" />
              
              <p className="text-xs text-gray-500 mt-1" data-id="element-9436">
                {t("components.AddRoomForm.standard_number_of_allowed_adults_for")}
              </p>
            </div>
            <div data-id="element-9437">
              <label htmlFor="baseChildrenOccupancy" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9438">
                
                {t("components.AddRoomForm.base_children_occupancy")}<span className="text-red-500" data-id="element-9439">*</span>
              </label>
              <input type="number" id="baseChildrenOccupancy" value={roomData.baseChildrenOccupancy} onChange={e => handleNumberChange('baseChildrenOccupancy', e.target.value)} min="0" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-9440" />
              
              <p className="text-xs text-gray-500 mt-1" data-id="element-9441">
                {t("components.AddRoomForm.standard_number_of_allowed_children_for")}
              </p>
            </div>
            <div data-id="element-9442">
              <label htmlFor="maxOccupancy" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9443">
                
                {t("components.AddRoomForm.maximum_occupancy")}<span className="text-red-500" data-id="element-9444">*</span>
              </label>
              <input type="number" id="maxOccupancy" value={roomData.maxOccupancy} onChange={e => handleNumberChange('maxOccupancy', e.target.value)} min="1" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-9445" />
              
              <p className="text-xs text-gray-500 mt-1" data-id="element-9446">
                {t("components.AddRoomForm.maximum_number_of_guests_allowed")}
              </p>
            </div>
          </div>
          <div className="space-y-2 mb-4" data-id="element-9447">
            <div className="flex items-center" data-id="element-9448">
              <input type="checkbox" id="allowAdditionalAdults" name="allowAdditionalAdults" checked={roomData.allowAdditionalAdults} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-9449" />
              
              <label htmlFor="allowAdditionalAdults" className="ml-2 text-sm text-gray-700" data-id="element-9450">
                
                {t("components.AddRoomForm.allow_additional_adults")}
              </label>
            </div>
            <p className="text-xs text-gray-500 ml-6" data-id="element-9451">
              {t("components.AddRoomForm.allow_additional_adults_not_exceeding_total")}
            </p>
            <div className="flex items-center mt-2" data-id="element-9452">
              <input type="checkbox" id="allowAdditionalChildren" name="allowAdditionalChildren" checked={roomData.allowAdditionalChildren} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-9453" />
              
              <label htmlFor="allowAdditionalChildren" className="ml-2 text-sm text-gray-700" data-id="element-9454">
                
                {t("components.AddRoomForm.allow_additional_children")}
              </label>
            </div>
            <p className="text-xs text-gray-500 ml-6" data-id="element-9455">
              {t("components.AddRoomForm.allow_additional_children_not_exceeding_total")}
            </p>
          </div>
          {/* Occupancy Summary */}
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4" data-id="element-9456">
            <div className="flex" data-id="element-9457">
              <AlertCircleIcon className="h-5 w-5 text-red-500 mr-2" data-id="element-9458" />
              <div data-id="element-9459">
                <p className="text-sm text-red-700" data-id="element-9460">
                  {t("components.AddRoomForm.this_room_has_a_base_occupancy")}{' '}
                  {roomData.baseAdultsOccupancy} {t("components.AddRoomForm.adults_and")}{' '}
                  {roomData.baseChildrenOccupancy} {t("components.AddRoomForm.children_with_a_total_maximum_occupancy")} {roomData.maxOccupancy}.
                </p>
                <p className="text-sm text-red-700" data-id="element-9461">
                  {t("components.AddRoomForm.additional_adults_are_not_allowed")}
                </p>
                <p className="text-sm text-red-700" data-id="element-9462">
                  {t("components.AddRoomForm.additional_children_are_not_allowed")}
                </p>
                <p className="text-sm text-red-700" data-id="element-9463">
                  {t("components.AddRoomForm.additional_cost_per_child_or_adults")}
                </p>
              </div>
            </div>
          </div>
        </div>
        {/* Bed Configuration */}
        <div className="mb-6" data-id="element-9464">
          <h3 className="text-md font-medium text-gray-800 mb-4" data-id="element-9465">
            {t("components.AddRoomForm.bed_configuration")}<span className="text-red-500" data-id="element-9466">*</span>
          </h3>
          {roomData.bedConfigurations.map((config, index) => <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 items-end" data-id="element-9467">
            
              <div data-id="element-9468">
                <label htmlFor={`bedType-${index}`} className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9469">
                
                  {t("components.AddRoomForm.bed_type")}
                </label>
                <div className="relative" data-id="element-9470">
                  <select id={`bedType-${index}`} value={config.type} onChange={e => handleBedConfigChange(index, 'type', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md appearance-none pr-8" data-id="element-9471">
                  
                    <option value="" data-id="element-9472">{t("components.AddRoomForm.select_bed_type")}</option>
                    {bedTypes.map(type => <option key={type} value={type} data-id="element-9473">
                        {type}
                      </option>)}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none" data-id="element-9474">
                    <svg className="w-4 h-4 fill-current text-gray-500" viewBox="0 0 20 20" data-id="element-9475">
                    
                      <path d="M7 7l3-3 3 3m0 6l-3 3-3-3" strokeWidth="1.5" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round" data-id="element-9476" />
                    
                    </svg>
                  </div>
                </div>
              </div>
              <div className="flex items-center" data-id="element-9477">
                <div className="flex-grow" data-id="element-9478">
                  <label htmlFor={`bedCount-${index}`} className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9479">
                  
                    {t("components.AddRoomForm.bed_count")}
                  </label>
                  <input type="number" id={`bedCount-${index}`} value={config.count} onChange={e => handleBedConfigChange(index, 'count', e.target.value)} min="1" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-9480" />
                
                </div>
                {index > 0 && <button type="button" onClick={() => removeBedConfig(index)} className="ml-2 mt-6 p-2 text-red-600 hover:text-red-800" data-id="element-9481">
                
                    <TrashIcon size={18} data-id="element-9482" />
                  </button>}
              </div>
            </div>)}
          <button type="button" onClick={addBedConfig} className="flex items-center text-blue-600 hover:text-blue-800" data-id="element-9483">
            
            <PlusIcon size={16} className="mr-1" data-id="element-9484" />
            {t("components.AddRoomForm.add_bed_configuration")}
          </button>
        </div>
        {/* Submit Button */}
        <div className="flex justify-end" data-id="element-9485">
          <button type="submit" className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700" data-id="element-9486">
            
            {t("components.AddRoomForm.save")}
          </button>
        </div>
      </form>
    </div>;
}